import React, { useState } from 'react';
import { portfolioData } from '../mock';
import { Download, Mail, Instagram, Phone } from 'lucide-react';
import { SketchStar } from '../components/SketchElements';
import { MaskingTape } from '../components/MaskingTape';

export const AboutPage = () => {
  const { about, contact } = portfolioData;
  const [copiedField, setCopiedField] = useState(null);

  const handleDownloadResume = () => {
    if (about.cvUrl) {
      window.open(about.cvUrl, '_blank');
    }
  };

  const copyToClipboard = (text, field) => {
    const showCopied = () => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    };

    // Fallback using a temporary textarea — works in iframes/non-secure contexts too
    const fallbackCopy = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      ta.style.pointerEvents = 'none';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      try {
        document.execCommand('copy');
        showCopied();
      } catch (_e) {
        /* noop */
      }
      document.body.removeChild(ta);
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(showCopied).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 relative overflow-hidden">
      {/* Paper grain texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.12]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='280' height='280'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Scattered red notebook stars */}
      {[
        { top: '12%', right: '8%', size: 20, rot:  15, delay: 0.4 },
        { top: '38%', left: '4%',  size: 16, rot: -10, delay: 0.6 },
        { top: '62%', right: '5%', size: 22, rot:  20, delay: 0.8 },
        { top: '88%', left: '10%', size: 18, rot:  -8, delay: 1.0 },
      ].map((s, i) => {
        const posStyle = {
          width: `${s.size}px`,
          height: `${s.size}px`,
          transform: `rotate(${s.rot}deg)`,
        };
        if (s.left) posStyle.left = s.left;
        if (s.right) posStyle.right = s.right;
        if (s.top) posStyle.top = s.top;
        return (
          <SketchStar
            key={i}
            className="absolute text-[color:var(--punk-red)]/80 hidden sm:block"
            style={posStyle}
            delay={s.delay}
          />
        );
      })}

      <div className="container mx-auto max-w-5xl px-6 relative z-10">
        {/* Title */}
        <div className="mb-12 animate-fade-in">
          <div className="relative inline-block">
            <h1 className="font-stencil text-5xl sm:text-6xl md:text-8xl text-white tracking-tight uppercase leading-[0.9]">
              About Me
            </h1>
            {/* Red marker underline — extends past the text on both sides */}
            <svg
              viewBox="0 0 400 26"
              preserveAspectRatio="none"
              className="absolute -left-[6%] -bottom-3 w-[112%] h-5 md:h-7 pointer-events-none"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M2,14 C30,8 70,18 110,11 C150,5 195,18 240,10 C285,4 330,17 370,11 C385,9 395,12 398,11"
                stroke="var(--punk-red)"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray="500"
                strokeDashoffset="500"
                style={{ animation: 'draw-path 1s cubic-bezier(0.65, 0, 0.35, 1) 0.4s forwards' }}
              />
              <path
                d="M5,17 C32,12 68,21 108,14 C148,9 192,21 238,13 C284,8 328,20 368,14 C384,12 394,15 396,14"
                stroke="var(--punk-red)"
                strokeWidth="5"
                strokeLinecap="round"
                opacity="0.65"
                strokeDasharray="500"
                strokeDashoffset="500"
                style={{ animation: 'draw-path 1s cubic-bezier(0.65, 0, 0.35, 1) 0.55s forwards' }}
              />
            </svg>
          </div>

          <p className="font-hand text-xl md:text-2xl text-white/80 mt-10 max-w-3xl leading-snug">
            {about.bio}
          </p>

          {/* Compact contact row — inline next to the bio */}
          <div
            className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 max-w-3xl"
            data-testid="about-contact-row"
          >
            {/* Email — copy to clipboard */}
            <button
              type="button"
              onClick={() => copyToClipboard(contact.email, 'email')}
              data-testid="contact-email-btn"
              className="relative group inline-flex items-center gap-2 text-white/70 hover:text-[color:var(--punk-red)] transition-colors"
              aria-label="Copy email"
            >
              <Mail className="w-4 h-4" />
              <span className="font-hand text-base md:text-lg underline decoration-white/30 decoration-1 underline-offset-[5px] group-hover:decoration-[color:var(--punk-red)]">
                {contact.email}
              </span>
              {copiedField === 'email' && (
                <span
                  className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 bg-[color:var(--punk-red)] text-white text-[10px] font-typewriter uppercase tracking-widest whitespace-nowrap z-20 animate-fade-in"
                  data-testid="copied-tooltip-email"
                >
                  Copied!
                </span>
              )}
            </button>
            <span className="text-white/25 hidden sm:inline" aria-hidden="true">·</span>

            {/* Instagram — copy to clipboard */}
            <button
              type="button"
              onClick={() => copyToClipboard(contact.social.instagram, 'instagram')}
              data-testid="contact-instagram-btn"
              className="relative group inline-flex items-center gap-2 text-white/70 hover:text-[color:var(--punk-red)] transition-colors"
              aria-label="Copy Instagram handle"
            >
              <Instagram className="w-4 h-4" />
              <span className="font-hand text-base md:text-lg underline decoration-white/30 decoration-1 underline-offset-[5px] group-hover:decoration-[color:var(--punk-red)]">
                {contact.social.instagram}
              </span>
              {copiedField === 'instagram' && (
                <span
                  className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 bg-[color:var(--punk-red)] text-white text-[10px] font-typewriter uppercase tracking-widest whitespace-nowrap z-20 animate-fade-in"
                  data-testid="copied-tooltip-instagram"
                >
                  Copied!
                </span>
              )}
            </button>
            <span className="text-white/25 hidden sm:inline" aria-hidden="true">·</span>

            {/* Phone — copy to clipboard */}
            <button
              type="button"
              onClick={() => copyToClipboard(contact.phone, 'phone')}
              data-testid="contact-phone-btn"
              className="relative group inline-flex items-center gap-2 text-white/70 hover:text-[color:var(--punk-red)] transition-colors"
              aria-label="Copy phone number"
            >
              <Phone className="w-4 h-4" />
              <span className="font-hand text-base md:text-lg underline decoration-white/30 decoration-1 underline-offset-[5px] group-hover:decoration-[color:var(--punk-red)]">
                {contact.phone}
              </span>
              {copiedField === 'phone' && (
                <span
                  className="absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 bg-[color:var(--punk-red)] text-white text-[10px] font-typewriter uppercase tracking-widest whitespace-nowrap z-20 animate-fade-in"
                  data-testid="copied-tooltip-phone"
                >
                  Copied!
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Skills - main concepts */}
        <div className="mb-12 animate-fade-in-delay">
          <h2 className="font-stencil text-2xl md:text-3xl text-white uppercase tracking-wider mb-6">
            Skills &amp; Expertise
          </h2>
          <div className="flex flex-wrap gap-3 md:gap-4">
            {about.skills.map((skill, index) => {
              const rot = index % 2 === 0 ? -1.5 : 1.5;
              return (
                <span
                  key={index}
                  className="font-marker text-base md:text-lg text-white px-4 py-1.5 border-2 border-white/40 hover:border-[color:var(--punk-red)] hover:bg-[color:var(--punk-red)]/10 transition-all duration-300 uppercase tracking-wide"
                  style={{ transform: `rotate(${rot}deg)` }}
                >
                  {skill}
                </span>
              );
            })}
          </div>
        </div>

        {/* Software - secondary tools list */}
        <div className="mb-16 animate-fade-in-delay">
          <h3 className="font-typewriter text-xs md:text-sm uppercase tracking-[0.3em] text-white/55 mb-4">
            Software
          </h3>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {about.software.map((tool, index) => (
              <span
                key={index}
                className="font-hand text-lg md:text-xl text-white/75 hover:text-white transition-colors"
              >
                {tool}
                {index < about.software.length - 1 && (
                  <span className="text-[color:var(--punk-red)] ml-3">·</span>
                )}
              </span>
            ))}
          </div>
        </div>

        {/* Experience timeline */}
        <div className="mb-16 animate-fade-in-delay">
          <h2 className="font-stencil text-2xl md:text-3xl text-white uppercase tracking-wider mb-6">
            Experience
          </h2>
          <div className="space-y-5">
            {about.experience.map((exp, index) => {
              const rot = index % 2 === 0 ? -0.5 : 0.5;
              return (
                <div
                  key={index}
                  className="relative bg-white/[0.03] border border-white/15 hover:border-[color:var(--punk-red)]/60 transition-colors p-6"
                  style={{ transform: `rotate(${rot}deg)` }}
                >
                  {/* Tape on top-right */}
                  <MaskingTape
                    width={70}
                    height={20}
                    rotation={index % 2 === 0 ? 14 : -12}
                    color="cream"
                    className="absolute -top-3"
                    style={{ right: '12%' }}
                  />
                  <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2">
                    <div>
                      <h3 className="font-stencil text-xl md:text-2xl text-white uppercase tracking-wide">
                        {exp.title}
                      </h3>
                      <p className="font-hand text-xl text-white/70 mt-1">@ {exp.company}</p>
                    </div>
                    <span className="font-typewriter text-xs uppercase tracking-widest text-white/55">
                      {exp.period}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Education */}
        <div className="mb-16 animate-fade-in-delay">
          <h2 className="font-stencil text-2xl md:text-3xl text-white uppercase tracking-wider mb-6">
            Education
          </h2>
          <div className="space-y-5">
            {about.education.map((edu, index) => (
              <div
                key={index}
                className="relative bg-white/[0.03] border border-white/15 hover:border-[color:var(--punk-red)]/60 transition-colors p-6"
                style={{ transform: 'rotate(0.4deg)' }}
              >
                <MaskingTape
                  width={70}
                  height={20}
                  rotation={-10}
                  color="cream"
                  className="absolute -top-3"
                  style={{ left: '14%' }}
                />
                <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2">
                  <div>
                    <h3 className="font-stencil text-xl md:text-2xl text-white uppercase tracking-wide">
                      {edu.degree}
                    </h3>
                    <p className="font-hand text-xl text-white/70 mt-1">@ {edu.school}</p>
                  </div>
                  <span className="font-typewriter text-xs uppercase tracking-widest text-white/55">
                    {edu.period}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Languages */}
        <div className="mb-16 animate-fade-in-delay">
          <h2 className="font-stencil text-2xl md:text-3xl text-white uppercase tracking-wider mb-6">
            Languages
          </h2>
          <div className="flex flex-wrap gap-3 md:gap-4">
            {about.languages.map((lang, index) => (
              <span
                key={index}
                className="font-marker text-base md:text-lg text-white px-4 py-1.5 border-2 border-[color:var(--punk-red)]/60 uppercase tracking-wide"
                style={{ transform: `rotate(${index % 2 === 0 ? -1 : 1}deg)` }}
              >
                {lang}
              </span>
            ))}
          </div>
        </div>

        {/* CV download */}
        <div className="text-center animate-fade-in-delay">
          <button
            onClick={handleDownloadResume}
            data-testid="download-resume-btn"
            className="punk-btn"
          >
            <Download className="w-5 h-5" />
            Download my CV
          </button>
        </div>
      </div>
    </div>
  );
};
